/**
 * UI wording and small derivations. No food-safety rules live here: every rule, limit and "why" comes from
 * @logwave/core; this file only picks which core text to show and phrases it without blame (PEOPLE_AND_NEGATION).
 */
import { libraryForState, NOT_CLEARED_FOR_COWORKER, requirementsCatalog, SICK_REPORT_BLOCK_TITLE, stateOrDefault, type Finding, type LogEntry, type QuickAction, type Requirement, type UsState } from "@logwave/core";
import { isoToLocalTime, prettyTime, relativeTime } from "./time.ts";
import type { CheckinOutcome, EmployeeWithOnboarding, Gate } from "../api.ts";
import type { IconName } from "../components/Icons.tsx";
import type { Tone } from "../components/ui.tsx";

/* ───────── the one-line why, and its library guide ───────── */

/** The first requirement's plain summary: why this check exists (ONTOLOGICAL_CENTER §7a). */
const CATALOGS = new Map<UsState, Requirement[]>();
/** A state's requirements, built once per state and kept (Utah's too: no Utah shortcut, fix/one-truth). */
export function requirementsOf(state?: UsState): Requirement[] {
  const st = stateOrDefault(state);
  let c = CATALOGS.get(st);
  if (!c) CATALOGS.set(st, (c = requirementsCatalog(st)));
  return c;
}
const LIBRARIES = new Map<UsState, ReturnType<typeof libraryForState>>();
/** A state's library (built once and kept). */
export function libraryOf(state?: UsState): ReturnType<typeof libraryForState> {
  const st = stateOrDefault(state);
  let c = LIBRARIES.get(st);
  if (!c) LIBRARIES.set(st, (c = libraryForState(st)));
  return c;
}

export function whyFor(requirementIds: string[] | undefined, state?: UsState): string | undefined {
  for (const id of requirementIds ?? []) {
    const r = requirementsOf(state).find((x) => x.id === id);
    if (r) return firstSentence(r.summary);
  }
  return undefined;
}

/** "The why in one line" (ROLE_ONTOLOGY): the first sentence of a long requirement summary, same rule as the phone (MP1). */
export function firstSentence(s: string): string {
  if (s.length <= 90) return s.trim();
  const m = s.match(/^(.+?[.!?;])(\s|$)/);
  return (m ? m[1]! : s).trim().replace(/;$/, ".");
}

/** The Library guide that covers any of these requirements. */
export function guideFor(requirementIds: string[] | undefined, state?: UsState): string | undefined {
  const ids = new Set(requirementIds ?? []);
  return libraryOf(state).find((t) => t.requirementIds.some((r) => ids.has(r)))?.id;
}
export const guidePath = (topicId: string) => `/learn/library/${encodeURIComponent(topicId)}`;

/** The sick-worker guide (exclude vs restrict), for the person in charge. */
export function sickWorkerGuide(state?: UsState): string {
  const lib = libraryOf(state);
  const t = lib.find((x) => x.category === "people") ?? lib.find((x) => x.requirementIds.includes("fc-employee-health"));
  return t ? guidePath(t.id) : "/learn/library?category=people";
}

/* ───────── findings, phrased as the next thing to do ───────── */

/** "Missed: X" → "Still needed: X". Names a person only where the action is theirs ("Sam hasn't checked in"). */
export function findingTitle(f: Finding): string {
  if (f.kind === "missed_check") return f.title.replace(/^Missed: /, "Still needed: ");
  if (f.kind === "failed_check") return f.title.replace(/^Out of limit with no corrective action: /, "Write down the fix: ");
  if (f.kind === "not_checked_in") return f.title.replace(/: hasn't done today's check-in$/, ": check-in still needed");
  return f.title;
}

/**
 * Core writes times in UTC ("Was due 06:00", "Reported 2026-09-27 08:01"). Show them in the location's local time.
 * NEEDS CORE: format with the location's timezone (schedule.ts findings()).
 */
export function findingDetail(f: Finding, tz: string): string {
  if (f.kind === "missed_check" && f.ref?.includes("@")) return `Was due ${prettyTime(f.ref.split("@")[1]!)}`;
  const m = /^Reported (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/.exec(f.detail);
  if (f.kind === "incident" && m) return `Reported ${relativeTime(`${m[1]}T${m[2]}:00Z`)} (${isoToLocalTime(`${m[1]}T${m[2]}:00Z`, tz)})`;
  return f.detail;
}

export function findingIcon(f: Finding): IconName {
  switch (f.kind) {
    case "missed_check": return "clock";
    case "failed_check": return "thermo";
    case "incident": return "report";
    case "review": return "camera";
    case "not_checked_in": return "hands";
    case "not_cleared": return "learn";
    case "credential": return "prove";
    default: return "alert";
  }
}

export const SEVERITY_GROUP: Record<Finding["severity"], string> = { urgent: "Do now", high: "Today", normal: "When you can" };

/** Where a route action ("do", "fix", "open") takes you in the web app. */
export function routeFor(a: QuickAction, f: Finding, myEmployeeId?: string): string | null {
  const t = a.target;
  if (a.kind === "do" && t?.type === "check") return `/do?check=${encodeURIComponent(t.id)}`;
  if (a.kind === "fix" && t?.type === "entry") return `/do/fix/${encodeURIComponent(t.id)}`;
  if (a.kind === "open") {
    if (t?.type === "incident") return `/report#${t.id}`;
    if (t?.type === "review") return "/paper/review";
    // "Add the certificate" (a Nevada place's person in charge needs a manager certificate): the person's page, with the
    // credential form already open on that card (People.tsx reads ?add=cfsm), even when it is me.
    if (t?.type === "employee" && f.requirementIds.includes("cfsm")) return `/people/${t.id}?add=cfsm`;
    if (t?.type === "employee") return t.id === myEmployeeId ? "/learn" : `/people/${t.id}`;
  }
  if (f.kind === "not_checked_in" && f.ref === myEmployeeId) return "/check-in";
  return null;
}

/* ───────── on-shift status: the action, never the symptom ───────── */

export function shiftStatus(gate: Gate): { tone: Tone; label: string; icon: IconName; excluded: boolean } {
  if (gate.ready) return { tone: "go", label: "Ready", icon: "check", excluded: false };
  const b = gate.blocking[0]!;
  if (b.kind === "not_checked_in") {
    // A coworker's view (core sickprivacy.ts): cleared or not, never why (fix S2).
    if (b.title === NOT_CLEARED_FOR_COWORKER.title) return { tone: "warn", label: "Not cleared yet", icon: "clock", excluded: false };
    if (/hasn't done/.test(b.title)) return { tone: "warn", label: "Needs check-in", icon: "clock", excluded: false };
    if (/bandage/.test(b.title)) return { tone: "warn", label: "Needs bandage", icon: "bandage", excluded: false };
    // They reported themself sick today (core shiftGate SICK_REPORT_BLOCK_TITLE): the action, never the symptom (T4).
    if (b.title === SICK_REPORT_BLOCK_TITLE) return { tone: "stop", label: "No food work today", icon: "x", excluded: true };
    if (/^Restrict/.test(b.detail)) return { tone: "stop", label: "Restricted", icon: "x", excluded: true };
    return { tone: "stop", label: "Excluded", icon: "x", excluded: true };
  }
  return { tone: "warn", label: "Not cleared yet", icon: "learn", excluded: false };
}

/* ───────── caught early, and controlled shifts (the north star, FRAMEWORK T12) ───────── */

/** Readings that were out of range and fixed: a win, not a failure (T3). */
export const caughtEarly = (entries: Pick<LogEntry, "evaluation" | "correctiveAction">[]) => entries.filter((e) => !e.evaluation.ok && !!e.correctiveAction).length;

export interface ShiftVerdict { employeeId: string; name: string; date: string; controlled: boolean; reason?: string }

/**
 * A controlled shift (ONTOLOGICAL_CENTER §4): the person checked in before their first food task, and every reading they
 * took was in range or fixed. Fabrication flags count as none until core scores them per shift.
 * A "shift" here is one person's day at the location with a check-in or a record.
 *
 * NEEDS CORE: this belongs next to completion() in @logwave/core so the API, phone and web agree (T7).
 */
export function controlledShifts(entries: Pick<LogEntry, "createdBy" | "createdAt" | "date" | "evaluation" | "correctiveAction" | "templateId">[], checkins: CheckinOutcome[], people: Pick<EmployeeWithOnboarding, "id" | "name" | "userId">[]): ShiftVerdict[] {
  const out: ShiftVerdict[] = [];
  const keys = new Set<string>();
  const byUser = new Map(people.filter((p) => p.userId).map((p) => [p.userId!, p]));
  for (const c of checkins) keys.add(`${c.employeeId}|${c.date}`);
  for (const e of entries) { const p = byUser.get(e.createdBy); if (p) keys.add(`${p.id}|${e.date}`); }
  for (const k of keys) {
    const [employeeId, date] = k.split("|") as [string, string];
    const p = people.find((x) => x.id === employeeId);
    if (!p) continue;
    const mine = entries.filter((e) => e.date === date && e.createdBy === p.userId && e.templateId !== "task_done").sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const ci = checkins.find((c) => c.employeeId === employeeId && c.date === date);
    let reason: string | undefined;
    if (!ci) reason = mine.length ? "Took readings without a check-in" : undefined;
    else if (mine[0] && ci.at > mine[0].createdAt) reason = "Checked in after the first reading";
    const open = mine.find((e) => !e.evaluation.ok && !e.correctiveAction);
    if (!reason && open) reason = "A reading is out of range with no fix yet";
    if (!ci && !mine.length) continue;
    out.push({ employeeId, name: p.name, date, controlled: !reason, ...(reason ? { reason } : {}) });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
}

/* ───────── money (managers only; staff never see prices) ───────── */
export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
